export const DEFERRED_OPTION = "未定・相談したい" as const;

export const GENERIC_PURPOSE_OPTIONS = [
  "問い合わせを増やしたい",
  "企業・サービスの信頼感を高めたい",
  "採用応募を増やしたい",
  "購入・申込みを増やしたい",
  "情報を整理して分かりやすくしたい",
  DEFERRED_OPTION,
] as const;

const PURPOSE_OPTIONS_BY_SITE_TYPE: Record<string, readonly string[]> = {
  "コーポレートサイト": [
    "問い合わせを増やしたい",
    "企業・サービスの信頼感を高めたい",
    "会社情報を分かりやすく伝えたい",
    "採用にも活用したい",
    DEFERRED_OPTION,
  ],
  "サービスサイト・LP": [
    "問い合わせを増やしたい",
    "資料請求・申込みを増やしたい",
    "サービスの違いを伝えたい",
    "広告からの成果を高めたい",
    DEFERRED_OPTION,
  ],
  "採用サイト": [
    "採用応募を増やしたい",
    "会社の魅力を伝えたい",
    "求人媒体からの流入を増やしたい",
    "応募前の不安を減らしたい",
    DEFERRED_OPTION,
  ],
  "ECサイト": [
    "購入・申込みを増やしたい",
    "商品の魅力を伝えたい",
    "カゴ落ちを減らしたい",
    "リピーターを増やしたい",
    DEFERRED_OPTION,
  ],
  "既存サイトのリニューアル": [
    "問い合わせを増やしたい",
    "情報を整理して分かりやすくしたい",
    "デザイン・信頼感を高めたい",
    "自社で更新しやすくしたい",
    DEFERRED_OPTION,
  ],
};

export function getPurposeOptionsForSiteTypes(siteTypes: readonly string[]) {
  const matchingTypes = siteTypes.filter((siteType) =>
    Object.hasOwn(PURPOSE_OPTIONS_BY_SITE_TYPE, siteType),
  );
  if (!matchingTypes.length) return [...GENERIC_PURPOSE_OPTIONS];
  return [
    ...new Set(matchingTypes.flatMap((siteType) => PURPOSE_OPTIONS_BY_SITE_TYPE[siteType])),
  ];
}

export const BASE_QUESTIONS = [
  {
    id: "content-purpose",
    label: "制作したいサイトと目的",
    question:
      "まず、どんなWebサイトを作りたいですか？ 今回かなえたいことも一緒に教えてください。",
    answerOptions: [
      "コーポレートサイト",
      "サービスサイト・LP",
      "採用サイト",
      "ECサイト",
      "既存サイトのリニューアル",
      DEFERRED_OPTION,
    ],
    answerOptionGroups: [
      {
        id: "site-type",
        label: "Webサイトの種類",
        options: [
          "コーポレートサイト",
          "サービスサイト・LP",
          "採用サイト",
          "ECサイト",
          "既存サイトのリニューアル",
          DEFERRED_OPTION,
        ],
      },
      {
        id: "purpose",
        label: "今回の目的",
        options: [
          ...GENERIC_PURPOSE_OPTIONS,
        ],
      },
    ],
    answerPlaceholder:
      "例：サービスサイトを刷新し、法人からの問い合わせを増やしたい",
    demoFollowUp: "その中でも、いちばん実現したいことはどれですか？",
    demoFollowUpOptions: [
      "問い合わせを増やしたい",
      "企業・サービスの信頼感を高めたい",
      "採用応募を増やしたい",
      "現時点では相談したい",
    ],
    demoFollowUpPlaceholder: "例：法人からの問い合わせ数を増やしたい",
  },
  {
    id: "audience-media",
    label: "相手・媒体・現状の課題",
    question:
      "次に、そのサイトを誰に届けたいですか？ 使いたい媒体や、今困っていることも教えてください。",
    answerOptions: [
      "法人・担当者向け",
      "一般消費者向け",
      "採用候補者向け",
      "自社Webサイト",
      "SNS",
      "検索広告・Web広告",
      "求人媒体",
      "問い合わせにつながらない",
      "情報が整理されていない",
      "デザインが古い",
      "自社で更新しづらい",
      DEFERRED_OPTION,
    ],
    answerOptionGroups: [
      {
        id: "target-audience",
        label: "届けたい相手",
        options: [
          "法人・担当者向け",
          "一般消費者向け",
          "採用候補者向け",
          DEFERRED_OPTION,
        ],
      },
      {
        id: "media",
        label: "検討している媒体",
        options: [
          "自社Webサイト",
          "SNS",
          "検索広告・Web広告",
          "求人媒体",
        ],
      },
      {
        id: "challenge",
        label: "現状の課題",
        options: [
          "問い合わせにつながらない",
          "情報が整理されていない",
          "デザインが古い",
          "自社で更新しづらい",
          DEFERRED_OPTION,
        ],
      },
    ],
    answerPlaceholder:
      "例：中小企業の担当者向けだが、サービスの違いが伝わらず問い合わせにつながっていない",
    demoFollowUp: "今の課題で、まず改善したいのはどこですか？",
    demoFollowUpOptions: [
      "問い合わせまでの導線",
      "情報の整理・見せ方",
      "デザイン・信頼感",
      "更新のしやすさ",
      "相談しながら整理したい",
    ],
    demoFollowUpPlaceholder: "例：サービスの違いが伝わる情報設計を優先したい",
  },
  {
    id: "service-scope",
    label: "希望する依頼範囲",
    question:
      "どこからどこまでお手伝いすればよさそうですか？ ご希望の範囲を教えてください。",
    answerOptions: [
      "企画・要件整理",
      "デザイン",
      "コーディング・システム開発",
      "原稿・写真などのコンテンツ制作",
      "公開後の運用・改善",
      "一括で依頼したい",
      DEFERRED_OPTION,
    ],
    answerOptionGroups: [
      {
        id: "production-scope",
        label: "依頼したい制作範囲",
        options: [
          "企画・要件整理",
          "デザイン",
          "コーディング・システム開発",
          "原稿・写真などのコンテンツ制作",
        ],
      },
      {
        id: "support-scope",
        label: "公開後の支援",
        options: [
          "公開後の運用・改善",
          DEFERRED_OPTION,
        ],
      },
      {
        id: "delivery-style",
        label: "依頼の進め方",
        options: ["一括で依頼したい"],
      },
    ],
    answerPlaceholder: "例：企画・構成からデザイン、実装まで一括で依頼したい",
    demoFollowUp: "依頼したい範囲の中で、特に重視したいのはどこですか？",
    demoFollowUpOptions: [
      "企画・導線設計",
      "デザイン品質",
      "機能・システム開発",
      "公開後の運用改善",
    ],
    demoFollowUpPlaceholder: "例：問い合わせにつながる導線設計を特に重視したい",
  },
  {
    id: "budget-timing",
    label: "予算感・希望開始時期",
    question:
      "最後に、今考えている予算感と、公開したい時期を教えてください。",
    answerOptions: [
      "まず費用感を知りたい",
      "50〜100万円程度",
      "100〜300万円程度",
      "300万円以上も検討可能",
      "3か月以内に公開したい",
      "半年以内に公開したい",
      DEFERRED_OPTION,
    ],
    answerOptionGroups: [
      {
        id: "budget",
        label: "予算感",
        options: [
          "まず費用感を知りたい",
          "50〜100万円程度",
          "100〜300万円程度",
          "300万円以上も検討可能",
          DEFERRED_OPTION,
        ],
      },
      {
        id: "timing",
        label: "希望開始時期",
        options: [
          "3か月以内に公開したい",
          "半年以内に公開したい",
          DEFERRED_OPTION,
        ],
      },
    ],
    answerPlaceholder: "例：まず費用感を確認し、半年以内の公開を希望している",
    demoFollowUp: "予算と公開時期では、どちらを優先したいですか？",
    demoFollowUpOptions: [
      "費用感を優先",
      "公開時期を優先",
      "提案を見て相談したい",
    ],
    demoFollowUpPlaceholder: "例：まず費用感を確認してから公開時期を決めたい",
  },
] as const;

export type BaseQuestion = (typeof BASE_QUESTIONS)[number];
export type BaseQuestionId = BaseQuestion["id"];
export type QuestionKind = "base" | "followup";

export interface AnswerOptionGroup {
  id: string;
  label: string;
  options: readonly string[];
}

export interface FlowAnswer {
  questionId: BaseQuestionId;
  kind: QuestionKind;
  content: string;
  freeText: string;
  selectedOptions: string[];
  selectedOptionsByGroup?: Record<string, string[]>;
  targetFieldKey?: string;
}

function selectedOptionsForGroup(answer: FlowAnswer | undefined, groupId: string) {
  return answer?.selectedOptionsByGroup?.[groupId] ?? [];
}

function selectedSiteTypes(answers: readonly FlowAnswer[]) {
  const answer = answers.find(
    (item) => item.kind === "base" && item.questionId === "content-purpose",
  );
  const grouped = selectedOptionsForGroup(answer, "site-type");
  if (grouped.length) return grouped;
  return answer?.selectedOptions.filter((option) =>
    Object.hasOwn(PURPOSE_OPTIONS_BY_SITE_TYPE, option),
  ) ?? [];
}

function withOptions(
  groups: readonly AnswerOptionGroup[],
  groupId: string,
  options: readonly string[],
) {
  return groups.map((group) =>
    group.id === groupId ? { ...group, options: [...options] } : {
      ...group,
      options: [...group.options],
    },
  );
}

/**
 * Returns the same fixed question groups with context-aware choices. The
 * server uses this function as the source of truth, while the client uses
 * getPurposeOptionsForSiteTypes for the immediate Q1 preview.
 */
export function getContextualOptionGroups(
  questionId: BaseQuestionId,
  answers: readonly FlowAnswer[],
): AnswerOptionGroup[] {
  const question = getBaseQuestion(questionId);
  if (!question) return [];
  const groups = question.answerOptionGroups;
  const siteTypes = selectedSiteTypes(answers);

  if (questionId === "audience-media") {
    if (siteTypes.includes("採用サイト")) {
      return withOptions(groups, "target-audience", [
        "採用候補者向け",
        "第二新卒・若手向け",
        "経験者・専門職向け",
        DEFERRED_OPTION,
      ]).map((group) =>
        group.id === "media"
          ? {
              ...group,
              options: [
                "自社採用サイト",
                "求人媒体",
                "採用SNS",
              ],
            }
          : group.id === "challenge"
            ? {
                ...group,
                options: [
                  "会社の魅力が伝わらない",
                  "仕事内容が伝わらない",
                  "応募につながらない",
                  "自社で更新しづらい",
                  DEFERRED_OPTION,
                ],
              }
            : group,
      );
    }
    if (siteTypes.includes("ECサイト")) {
      return withOptions(groups, "target-audience", [
        "一般消費者向け",
        "法人・担当者向け",
        "リピーター向け",
        DEFERRED_OPTION,
      ]).map((group) =>
        group.id === "media"
          ? {
              ...group,
              options: [
                "自社EC",
                "ECモール",
                "SNS",
                "検索広告・Web広告",
              ],
            }
          : group.id === "challenge"
            ? {
                ...group,
                options: [
                  "商品理解が進まない",
                  "購入につながらない",
                  "カゴ落ちが多い",
                  "自社で更新しづらい",
                  DEFERRED_OPTION,
                ],
              }
            : group,
      );
    }
  }

  if (questionId === "service-scope") {
    const productionOptions = siteTypes.includes("採用サイト")
      ? [
          "企画・要件整理",
          "採用ページ・コンテンツ制作",
          "デザイン",
          "コーディング・システム開発",
          "原稿・写真などのコンテンツ制作",
        ]
      : siteTypes.includes("ECサイト")
        ? [
            "企画・要件整理",
            "商品登録・EC運用設計",
            "デザイン",
            "コーディング・システム開発",
            "原稿・写真などのコンテンツ制作",
          ]
        : groups.find((group) => group.id === "production-scope")?.options ?? [];
    return withOptions(groups, "production-scope", productionOptions);
  }

  if (questionId === "budget-timing") {
    const timingOptions = siteTypes.includes("採用サイト")
      ? ["採用計画に合わせたい", "求人掲載開始に合わせたい", DEFERRED_OPTION]
      : siteTypes.includes("ECサイト")
        ? ["繁忙期前に公開したい", "キャンペーンに合わせたい", DEFERRED_OPTION]
        : groups.find((group) => group.id === "timing")?.options ?? [];
    return withOptions(groups, "timing", timingOptions);
  }

  return groups.map((group) => ({ ...group, options: [...group.options] }));
}

export function getBaseQuestion(questionId: BaseQuestionId) {
  return BASE_QUESTIONS.find((question) => question.id === questionId);
}
